import React, { createContext, useContext, useEffect, useRef, useState } from 'react'

import { Divider } from '@mui/material'

import TableFilters from '../components/filters/TableFilters'
import DataTable from '../components/tables/DataTable'
import TableActionsInput from '../components/inputs/TableActionsInput'
import reportApi from '../api/table'
import useHandleError from '../api/useHandleError'
import { getSessionFilters } from '../useSessionFilters'

import { AppSettingsContext } from '../App'
import { setViewportWidth } from '../useViewPort'

export const RowsContext = createContext()

const TablePage = () => {
  const sessionFilters = getSessionFilters() || {}
  const { setSelectedRows, selectedRows } = useContext(AppSettingsContext)

  const [isLoading, setIsLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [isResultEmpty, setIsResultEmpty] = useState(false)
  const [filters, setFilters] = useState(sessionFilters)
  const [currentPage, setCurrentPage] = useState(2)
  const [maxRowsCount, setMaxRowsCount] = useState(null)
  const [isRefreshed, setIsRefreshed] = useState(false)
  const handleError = useHandleError()
  const actualPageDataRef = useRef(null)
  setTimeout(() => setViewportWidth())

  const getRows = async (usedFilters) => {
    setIsLoading(true)
    const params = { ...usedFilters }
    let actualPage = null
    if (currentPage === 2) {
      actualPage = 0
      actualPageDataRef.current = null
    } else {
      actualPage = currentPage - 1
    }

    /* Preload two pages at the start */
    let pages = []
    while (actualPage < currentPage) {
      actualPage++
      if (actualPage !== 1) {
        const url = new URL(actualPageDataRef.current?.next)
        params.cursor = url.searchParams.get('cursor')
      }

      actualPageDataRef.current = await reportApi
        .getReports(params)
        .then((data) => {
          if (actualPage === 1 && data.status === 204) {
            setIsResultEmpty(true)
            return {
              count: 0,
              next: null,
              previous: null,
              results: [],
            }
          }
          setIsResultEmpty(false)
          return data.data
        })
        .catch(handleError)
      if (actualPageDataRef.current?.count >= 0) {
        setMaxRowsCount(actualPageDataRef.current.count)
      }
      pages = [...pages, ...actualPageDataRef.current.results]
      if (actualPageDataRef.current.next === null) {
        break
      }
    }
    return { ...actualPageDataRef.current, results: pages }
  }

  useEffect(() => {
    const fetchData = async () => {
      if (currentPage) {
        const res = await getRows(filters)
        if (res) {
          const rowsData = [...rows, ...res.results]
          setRows(rowsData)
        }
      }
      setIsLoading(false)
    }
    fetchData()
  }, [currentPage, isRefreshed])

  const getNewData = async () => {
    setCurrentPage(2)
    setIsRefreshed(!isRefreshed)
    setSelectedRows(new Set())
    setIsLoading(false)
    setRows([])
  }

  const previousFilters = useRef(sessionFilters)
  useEffect(() => {
    const hasFiltersChanged = JSON.stringify(previousFilters.current) !== JSON.stringify(filters)
    if (hasFiltersChanged) {
      setSelectedRows(new Set())
      getNewData()
    }
    previousFilters.current = filters
  }, [filters])

  useEffect(() => {
    return () => {
      setSelectedRows(new Set())
    }
  }, [])

  return (
    <RowsContext.Provider
      value={{
        selectedRows,
        setSelectedRows,
        rows,
        setRows,
        isResultEmpty,
        setIsLoading,
        isLoading,
        currentPage,
        setCurrentPage,
        maxRowsCount,
        filters,
        getNewData,
        setFilters,
      }}
    >
      <TableFilters setFilters={setFilters} usedFilters={filters} />
      <Divider />
      <TableActionsInput />
      <DataTable />
    </RowsContext.Provider>
  )
}

export default TablePage
